import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Nfc, RotateCw, QrCode, X } from "lucide-react";
import { formatNig, fmtDate } from "@/lib/api";

export default function DigitalCard({ user, komsel }) {
  const [flipped, setFlipped] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [shine, setShine] = useState({ x: 30, y: 20 });
  const ref = useRef(null);
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    setShine({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };
  const face = "absolute inset-0 rounded-3xl overflow-hidden backface-hidden shadow-2xl shadow-blue-900/30";

  return (
    <div className="px-5 -mt-12 relative z-10" style={{ perspective: 1200 }}>
      <motion.div
        ref={ref}
        role="button"
        tabIndex={0}
        onPointerMove={onMove}
        onClick={() => setFlipped((f) => !f)}
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ type: "spring", stiffness: 140, damping: 18 }}
        className="relative w-full aspect-[1.586/1] preserve-3d text-left cursor-pointer"
        data-testid="gim-digital-card"
        aria-label="Balik kartu"
      >
        <div className={`${face} bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white p-6 flex flex-col`} data-testid="digital-card-front">
          <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(380px circle at ${shine.x}% ${shine.y}%, rgba(255,255,255,.2), transparent 40%)` }} />
          <div className="absolute -right-10 -bottom-14 w-48 h-48 rounded-full border-[18px] border-white/5" />
          <div className="relative flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm font-extrabold tracking-tight"><span className="w-7 h-7 rounded-lg bg-white grid place-items-center overflow-hidden"><img src="/logo-gim.png" alt="GIM" className="w-5 h-5 object-contain" /></span>GIM Digital Card</span>
            <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-400"><span className="relative w-1.5 h-1.5 rounded-full bg-emerald-500 pulse-dot" />Aktif</span>
          </div>
          <div className="relative mt-5 flex items-center gap-3">
            <div className="w-11 h-8 rounded-md bg-gradient-to-br from-amber-100 to-amber-400/80" />
            <Nfc className="w-5 h-5 text-white/50" />
          </div>
          <p data-testid="digital-card-nig" className="relative mt-auto text-xl font-bold tracking-[0.14em] tabular-nums">{formatNig(user.nig)}</p>
          <div className="relative mt-3 flex items-end justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">Nama Jemaat</p>
              <p className="font-bold" data-testid="digital-card-name">{user.name}</p>
              <p className="text-[11px] text-white/50 mt-0.5">Sejak {fmtDate(user.created_at, { month: "short", year: "numeric" })}{komsel ? ` · ${komsel.name}` : ""}</p>
            </div>
            <span className="flex items-center gap-1 text-[10px] font-bold text-white/60"><RotateCw className="w-3 h-3" /> Ketuk</span>
          </div>
        </div>

        <div className={`${face} bg-white p-5 flex flex-col items-center justify-center gap-3 text-center [transform:rotateY(180deg)] border border-slate-100`} data-testid="digital-card-back">
          <p className="text-[10px] font-bold uppercase tracking-widest text-blue-600">QR Check-in</p>
          <p className="text-3xl font-black text-slate-900 tabular-nums tracking-tight">{user.nig}</p>
          <button type="button" onClick={(e) => { e.stopPropagation(); setQrOpen(true); }} data-testid="digital-card-show-qr-back"
            className="inline-flex items-center gap-2 bg-slate-900 text-white rounded-full px-5 py-2.5 text-xs font-bold active:scale-95 transition">
            <QrCode className="w-4 h-4" /> Tampilkan QR Absen
          </button>
          <p className="text-[11px] text-slate-400 px-4">Tunjukkan QR ini ke petugas OPC saat ibadah untuk absen otomatis.</p>
        </div>
      </motion.div>

      <button type="button" onClick={() => setQrOpen(true)} data-testid="digital-card-show-qr"
        className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-slate-900 text-white rounded-2xl py-3 text-sm font-bold active:scale-95 transition">
        <QrCode className="w-4 h-4" /> Tampilkan QR Absen
      </button>

      {createPortal(
        <AnimatePresence>
          {qrOpen && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[80] grid place-items-center bg-slate-900/70 backdrop-blur-sm p-6"
              onClick={() => setQrOpen(false)} data-testid="digital-card-qr-modal">
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-xs bg-white rounded-3xl p-6 text-center shadow-2xl">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-black text-slate-900">QR Absen Ibadah</p>
                  <button onClick={() => setQrOpen(false)} data-testid="digital-card-qr-close" className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
                </div>
                <div className="mt-4 grid place-items-center bg-white p-4 rounded-2xl border border-slate-100">
                  <QRCodeSVG value={`GIM:${user.nig}`} size={240} fgColor="#0f172a" level="M" data-testid="digital-card-qr" />
                </div>
                <p className="mt-4 text-2xl font-black text-slate-900 tabular-nums tracking-wider">{formatNig(user.nig)}</p>
                <p className="mt-1 text-xs text-slate-500">{user.name}{komsel ? ` · ${komsel.name}` : ""}</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
